/**
 * Generate Scene view (spec sections 4, 15, 28, 52, 53, 54).
 *
 * GENERATE SCENE creates a reusable visual asset.
 * CREATE BEX CAST (the Studio) consumes one.
 * They are deliberately separate operations and the copy says so.
 */

import { h } from '../../utils/dom.js';
import { formatUsd } from '../../utils/format.js';
import { RESEARCH, LIMITS } from '../../core/config.js';
import { generatePrompt, generateVidsPrompt, summarizePrompt } from '../../prompts/prompt-generator.js';
import { describeEffect } from '../../audio/effects.js';
import { createScenePreview } from '../components/scene-preview.js';
import { card, button, linkButton, note, kv, segmented, sectionHeading, chipRow } from '../components/common.js';
import { toastOk, toastError } from '../components/toast.js';

const PROVIDERS = [
  { id: 'google-vids', label: 'Google Vids', hint: 'Manual, official' },
  { id: 'google-veo', label: 'Google Veo', hint: 'API, billable' },
  { id: 'local-library', label: 'Import file', hint: 'You already have it' },
];

export function renderGenerate(ctx) {
  const { store, actions, sceneStore, promptLibrary, registry, settings } = ctx;
  const state = store.get();
  const root = h('div', { class: 'stack-lg' });

  const scenes = sceneStore.all();
  const pending = sceneStore.pending();
  const currentSceneId = state.generate?.scene?.id ?? state.selectedScene?.id ?? pending[0]?.id ?? scenes[0]?.id ?? null;
  const scene = sceneStore.get(currentSceneId);
  const providerId = state.generateProviderId ?? 'google-vids';

  root.append(
    sectionHeading(
      'Generate Scene',
      'Create a reusable loop asset for the Scene Library',
      button('Go to Studio', { kind: 'ghost', small: true, onClick: () => actions.setView('studio') }),
    ),
  );

  root.append(
    card([
      h('div', { class: 'stack' }, [
        h('div', { class: 'row' }, [
          h('span', { class: 'chip chip--accent' }, ['GENERATE SCENE']),
          h('span', { class: 'tiny dim' }, ['creates an asset, reusable forever']),
        ]),
        h('div', { class: 'row' }, [
          h('span', { class: 'chip' }, ['CREATE BEX CAST']),
          h('span', { class: 'tiny dim' }, ['consumes an asset to produce an episode']),
        ]),
        h('p', { class: 'tiny dim', style: { margin: 0 } }, [
          'The Studio never regenerates a scene automatically. It reuses what the library already has (spec 52/53).',
        ]),
      ]),
    ]),
  );

  /* ------------------------------------------------ scene picker */

  const sceneOptions = [
    { id: '__pending', label: 'Needs an asset', hint: `${pending.length} scenes` },
    ...(pending.length ? [] : []),
  ];

  const sceneSelect = h(
    'select',
    {
      'aria-label': 'Scene to generate',
      onchange: (event) => {
        const id = event.target.value;
        const next = sceneStore.get(id);
        store.set({ generate: { ...(state.generate ?? {}), scene: next, promptEntry: promptLibrary.preferredForScene(id, providerId) } });
        actions.render();
      },
    },
    scenes.map((item) =>
      h(
        'option',
        { value: item.id, selected: item.id === currentSceneId },
        [`${item.name} - ${isReady(item) ? 'has asset' : 'needs asset'}`],
      ),
    ),
  );

  const promptEntry = promptLibrary.preferredForScene(currentSceneId, providerId);
  const promptList = promptLibrary.forScene(currentSceneId);

  const promptSelect = h(
    'select',
    {
      'aria-label': 'Prompt variant',
      onchange: (event) => {
        const chosen = promptList.find((p) => p.id === event.target.value) ?? null;
        store.set({ generate: { ...(state.generate ?? {}), scene, promptEntry: chosen } });
        actions.render();
      },
    },
    promptList.map((item) =>
      h('option', { value: item.id, selected: item.id === promptEntry?.id }, [`${item.id} (${item.duration}s, ${item.style})`]),
    ),
  );

  const durationSeconds = Number(promptEntry?.duration ?? scene?.duration ?? 8) || 8;
  const veoPrompt = scene
    ? generatePrompt({
        scene,
        promptEntry,
        mood: settings.mood,
        style: promptEntry?.style ?? scene.visualStyle,
        durationSeconds,
        aspectRatio: '9:16',
        resolution: settings.veoResolution,
        loopMode: settings.loopMode,
        providerId: providerId === 'google-veo' ? 'google-veo' : 'google-vids',
      })
    : '';

  const vidsPrompt = scene
    ? generateVidsPrompt({
        scene,
        promptEntry,
        mood: settings.mood,
        style: promptEntry?.style ?? scene.visualStyle,
        durationSeconds,
        aspectRatio: '9:16',
        resolution: null,
        loopMode: settings.loopMode,
      })
    : '';

  const activePrompt = providerId === 'google-veo' ? veoPrompt : vidsPrompt;

  const preview = scene
    ? createScenePreview(scene, { width: 200, height: 356, animated: true, strength: 0 })
    : null;
  if (preview) ctx.onDispose(() => preview.dispose());

  root.append(
    card(
      [
        h('div', { class: 'field' }, [
          h('label', { class: 'field__label' }, ['Scene']),
          sceneSelect,
        ]),
        scene
          ? h('div', { class: 'preview' }, [
              h('div', { class: 'preview__frame' }, [preview.element]),
              h('div', { class: 'preview__meta' }, [
                h('div', { class: 'preview__title' }, [scene.name]),
                h('div', { class: 'tiny dim' }, [scene.description]),
                chipRow([
                  h('span', { class: 'chip' }, [`${scene.duration}s`]),
                  h('span', { class: `chip ${isReady(scene) ? 'chip--ok' : 'chip--warn'}` }, [
                    isReady(scene) ? 'asset present' : 'no asset yet',
                  ]),
                  h('span', { class: 'chip' }, [describeEffect(scene.audioEffect ?? 'none', 0).slice(0, 24)]),
                ]),
              ]),
            ])
          : note('No scenes available.', 'warn'),
        promptList.length
          ? h('div', { class: 'field' }, [h('label', { class: 'field__label' }, ['Prompt variant']), promptSelect])
          : note('This scene has no prompt yet. Add one in the scene editor.', 'warn'),
      ],
      { title: 'Target scene', step: 1 },
    ),
  );

  /* ------------------------------------------------ cache check (spec 53) */

  if (scene && isReady(scene)) {
    root.append(
      card(
        [
          note(
            `${scene.name} already has a usable asset. Reusing it costs nothing and takes no time. Only generate again if you deliberately want a new variation.`,
            'ok',
          ),
          h('div', { class: 'btn-row' }, [
            button('Use existing asset in Studio', {
              kind: 'primary',
              onClick: () => {
                actions.selectSceneById(scene.id);
                actions.setView('studio');
              },
            }),
            button('Generate a new variation anyway', {
              kind: 'ghost',
              onClick: () => store.set({ forceRegenerate: true }),
            }),
          ]),
        ],
        { title: 'Scene generation cache' },
      ),
    );
  }

  /* ------------------------------------------------ provider */

  root.append(
    card(
      [
        segmented(PROVIDERS, providerId, (id) => {
          store.set({ generateProviderId: id, forceRegenerate: false });
          actions.render();
        }),
        providerBlurb(providerId, registry),
      ],
      { title: 'Provider', step: 2 },
    ),
  );

  /* ------------------------------------------------ manual vids workflow */

  if (providerId === 'google-vids' && scene) {
    const provider = registry.get('google-vids');
    const research = provider.getResearch();
    const promptBox = h('pre', { class: 'prompt-box', id: 'vids-prompt' }, [activePrompt]);

    root.append(
      card(
        [
          h('div', { class: 'btn-row' }, [
            button('Copy prompt', {
              kind: 'primary',
              onClick: async () => {
                const ok = await ctx.actions.copyText(activePrompt);
                if (ok) toastOk('Prompt copied. Now open Google Vids and paste it.');
                else toastError('Clipboard is blocked in this browser. Select the text and copy manually.');
              },
            }),
            linkButton('Open Google Vids', research.url, { kind: 'ghost' }),
          ]),
          promptBox,
          h('div', { class: 'field__hint' }, [
            `${activePrompt.length} characters \u00b7 ${summarizePrompt(activePrompt, 140)}`,
          ]),
          note(
            'BEX LOOP does not scrape, automate or reverse-engineer Google Vids. The button above is a plain link to the official site and no Google credential is ever requested.',
            'info',
          ),
          h('div', { class: 'divider' }),
          h('h4', {}, ['Manual workflow']),
          h(
            'ol',
            { class: 'small muted', style: { paddingLeft: '20px', margin: '6px 0' } },
            [
              'Press COPY PROMPT.',
              'Press OPEN GOOGLE VIDS and sign in yourself.',
              'Create a new video and generate a clip using the prompt.',
              'Download the generated clip to your device.',
              'Return here and press IMPORT GENERATED VIDEO.',
              'Mark the loop quality and save it to the Scene Library.',
            ].map((step) => h('li', {}, [step])),
          ),
          button('Import generated video', {
            kind: 'primary',
            block: true,
            large: true,
            onClick: () => actions.triggerImport(scene.id, 'google-vids'),
          }),
          h('div', { class: 'divider' }),
          h('h4', {}, ['Current Google Vids limits (researched, not promised)']),
          h('div', { class: 'stack' }, research.notes.map((line) => h('div', { class: 'tiny dim' }, [`\u2022 ${line}`]))),
          h('div', { class: 'tiny dim' }, [
            `Verified ${research.checkedOn} \u00b7 `,
            h('a', { href: research.sources[0], target: '_blank', rel: 'noopener noreferrer', style: { color: 'var(--accent)' } }, ['official source']),
          ]),
        ],
        { title: 'Manual AI generation', step: 3 },
      ),
    );
  }

  /* ------------------------------------------------ automated veo */

  if (providerId === 'google-veo' && scene) {
    const provider = registry.get('google-veo');
    const model = settings.veoModel;
    const resolution = settings.veoResolution;
    const veoDuration = settings.veoDurationSeconds;
    const cost = provider.estimateGenerationCost({ model, resolution, durationSeconds: veoDuration });
    const configured = Boolean(settings.workerUrl);
    const status = state.providerStatus?.['google-veo'];

    root.append(
      card(
        [
          configured
            ? note('Worker configured. The API key stays in the Cloudflare Worker and never reaches this page.', 'ok')
            : note(
                'Google Veo needs a Cloudflare Worker to hold the API key. Deploy worker/index.js, set GEMINI_API_KEY as a Worker secret, then paste the Worker URL in Settings.',
                'warn',
              ),
          h('div', { class: 'field' }, [
            h('label', { class: 'field__label' }, ['Model']),
            h(
              'select',
              { onchange: (event) => actions.setSetting({ veoModel: event.target.value }) },
              RESEARCH.googleVeo.models.map((id) =>
                h('option', { value: id, selected: id === model }, [id]),
              ),
            ),
          ]),
          h('div', { class: 'field' }, [
            h('label', { class: 'field__label' }, ['Resolution']),
            segmented(
              RESEARCH.googleVeo.resolutions.map((r) => ({ id: r, label: r })),
              resolution,
              (id) => actions.setSetting({ veoResolution: id }),
            ),
          ]),
          h('div', { class: 'field' }, [
            h('label', { class: 'field__label' }, ['Duration (seconds)']),
            segmented(
              LIMITS.veoDurations.map((d) => ({ id: d, label: d })),
              veoDuration,
              (id) => actions.setSetting({ veoDurationSeconds: id }),
            ),
          ]),
          kv([
            ['Aspect ratio', '9:16 (vertical)'],
            ['Estimated cost', cost ? `${formatUsd(cost.totalUsd)} at ${formatUsd(cost.pricePerSecond)}/s` : 'unknown'],
            ['Free tier', 'None published for any Veo model'],
            ['Worker status', status?.detail ?? 'not checked yet'],
            ['Video retention', '2 days on the provider side - download promptly'],
          ]),
          note(
            'A confirmation sheet with the estimated cost appears before anything is sent. Nothing is generated silently.',
            'info',
          ),
          h('div', { class: 'btn-row' }, [
            button('Check provider status', {
              kind: 'ghost',
              onClick: async () => {
                await actions.refreshProviderStatus();
                actions.render();
              },
            }),
            button('Generate (billable)', {
              kind: 'primary',
              disabled: !configured,
              onClick: () =>
                actions.runAutomatedGeneration({
                  scene,
                  promptEntry,
                  prompt: veoPrompt,
                  model,
                  durationSeconds: veoDuration,
                  aspectRatio: '9:16',
                  resolution,
                }),
            }),
          ]),
          h('div', { class: 'field__hint' }, [
            'Veo is asynchronous: latency is documented as 11 seconds minimum and up to 6 minutes at peak.',
          ]),
        ],
        { title: 'Automated generation', step: 3 },
      ),
    );
  }

  /* ------------------------------------------------ local import */

  if (providerId === 'local-library' && scene) {
    root.append(
      card(
        [
          note('Import a clip you already have. It is validated, then stored in this browser.', 'info'),
          button('Choose video file', {
            kind: 'primary',
            block: true,
            large: true,
            onClick: () => actions.triggerImport(scene.id, 'local-library'),
          }),
          h('div', { class: 'field__hint' }, [
            'MP4, WebM or MOV. Ideal loop length is 8-15 seconds at 9:16.',
          ]),
        ],
        { title: 'Import from device', step: 3 },
      ),
    );
  }

  if (preview) queueMicrotask(() => preview.start());
  return root;
}

function providerBlurb(providerId, registry) {
  const provider = registry.get(providerId);
  if (!provider) return null;
  const caps = provider.capabilities;
  return h('div', { class: 'stack' }, [
    h('p', { class: 'small muted', style: { margin: 0 } }, [provider.description]),
    chipRow([
      h('span', { class: 'chip' }, [caps.type]),
      h('span', { class: 'chip' }, [caps.automated ? 'automated' : 'manual step']),
      h('span', { class: 'chip' }, [caps.requiresApiKey ? 'needs API key (server-side)' : 'no API key']),
      h('span', { class: `chip ${caps.costModel === 'none' ? 'chip--ok' : caps.costModel === 'free-tier' ? 'chip--info' : 'chip--warn'}` }, [caps.costModel]),
    ]),
    provider.docsUrl
      ? h('a', { href: provider.docsUrl, target: '_blank', rel: 'noopener noreferrer', class: 'tiny', style: { color: 'var(--accent)' } }, ['Official documentation'])
      : null,
  ]);
}

function isReady(scene) {
  if (scene.assetType === 'procedural') return true;
  if (scene.assetType === 'video') return Boolean(scene.assetUrl);
  return false;
}
